import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

interface ProfileData {
  id: string;
  name: string;
  email: string;
  avatar_url?: string;
  created_at: string;
  total_votes: number;
  vote_streak: number;
  favorite_model?: string;
}

interface VoteHistoryItem {
  id: string;
  battle_id: string;
  voted_at: string;
  clip_title: string;
  your_vote: 'model_a' | 'model_b' | 'tie' | 'skip';
  model_a_name: string;
  model_b_name: string;
  outcome: string;
}

interface VoteHistoryResponse {
  votes: VoteHistoryItem[];
  total: number;
  limit: number;
  offset: number;
}

export default function ProfilePage() {
  const { user, authToken } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [votes, setVotes] = useState<VoteHistoryItem[]>([]);
  const [votesTotal, setVotesTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [isLoadingVotes, setIsLoadingVotes] = useState(true);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [votesError, setVotesError] = useState<string | null>(null);

  const limit = 20;

  useEffect(() => {
    if (!user) {
      navigate('/');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!authToken) return;

    setIsLoadingProfile(true);
    setProfileError(null);

    fetch(`${API_BASE}/profile`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load profile');
        return res.json();
      })
      .then((data: ProfileData) => {
        setProfile(data);
      })
      .catch((err) => {
        setProfileError(err.message);
      })
      .finally(() => {
        setIsLoadingProfile(false);
      });
  }, [authToken]);

  useEffect(() => {
    if (!authToken) return;

    setIsLoadingVotes(true);
    setVotesError(null);

    fetch(`${API_BASE}/profile/votes?limit=${limit}&offset=${offset}`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load vote history');
        return res.json();
      })
      .then((data: VoteHistoryResponse) => {
        setVotes(data.votes);
        setVotesTotal(data.total);
      })
      .catch((err) => {
        setVotesError(err.message);
      })
      .finally(() => {
        setIsLoadingVotes(false);
      });
  }, [authToken, offset]);

  if (!user) return null;

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getVoteLabel = (vote: string) => {
    switch (vote) {
      case 'model_a':
        return 'Model A';
      case 'model_b':
        return 'Model B';
      case 'tie':
        return 'Tie';
      case 'skip':
        return 'Skipped';
      default:
        return vote;
    }
  };

  const getOutcomeStyle = (outcome: string) => {
    switch (outcome) {
      case 'correct':
        return 'text-green-400';
      case 'incorrect':
        return 'text-red-400';
      case 'tie':
        return 'text-arena-muted';
      default:
        return 'text-arena-muted';
    }
  };

  const hasPrevPage = offset > 0;
  const hasNextPage = offset + limit < votesTotal;

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-4xl mx-auto">
        {/* Profile Card */}
        {isLoadingProfile ? (
          <div className="card p-8 mb-8">
            <div className="flex items-center justify-center">
              <div className="w-8 h-8 border-4 border-arena-accent border-t-transparent rounded-full animate-spin"></div>
            </div>
          </div>
        ) : profileError ? (
          <div className="card p-8 mb-8">
            <div className="text-center text-red-400">{profileError}</div>
          </div>
        ) : profile ? (
          <div className="card p-8 mb-8">
            <div className="flex items-center gap-6">
              {profile.avatar_url ? (
                <img
                  src={profile.avatar_url}
                  alt={profile.name}
                  className="w-20 h-20 rounded-full object-cover"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-arena-accent/20 flex items-center justify-center text-arena-accent text-2xl font-bold">
                  {getInitials(profile.name)}
                </div>
              )}
              <div>
                <h1 className="text-2xl font-bold">{profile.name}</h1>
                <p className="text-arena-muted">{profile.email}</p>
                <p className="text-arena-muted text-sm mt-1">
                  Member since {formatDate(profile.created_at)}
                </p>
              </div>
            </div>
          </div>
        ) : null}

        {/* Stats Row */}
        {profile && (
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="card p-6 text-center">
              <div className="text-3xl font-bold font-mono">
                {profile.total_votes}
              </div>
              <div className="text-sm text-arena-muted mt-1">Total Votes</div>
            </div>
            <div className="card p-6 text-center">
              <div className="text-3xl font-bold font-mono">
                {profile.vote_streak}
              </div>
              <div className="text-sm text-arena-muted mt-1">Vote Streak</div>
            </div>
            <div className="card p-6 text-center">
              <div className="text-xl font-bold truncate">
                {profile.favorite_model || '--'}
              </div>
              <div className="text-sm text-arena-muted mt-1">
                Favorite Model
              </div>
            </div>
          </div>
        )}

        {/* Upload CTA */}
        <div className="flex justify-end mb-6">
          <Link to="/upload" className="btn btn-primary">
            Upload a Clip
          </Link>
        </div>

        {/* Vote History */}
        <div className="card overflow-hidden">
          <div className="p-4 border-b border-arena-border">
            <h2 className="font-semibold">Vote History</h2>
          </div>

          {isLoadingVotes ? (
            <div className="p-8 text-center text-arena-muted">
              <div className="w-6 h-6 border-4 border-arena-accent border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
              Loading votes...
            </div>
          ) : votesError ? (
            <div className="p-8 text-center text-red-400">{votesError}</div>
          ) : votes.length === 0 ? (
            <div className="p-8 text-center text-arena-muted">
              <p className="mb-4">No votes yet. Start judging some battles!</p>
              <Link to="/battle" className="btn btn-secondary">
                Go to Battle
              </Link>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-arena-border text-arena-muted text-sm">
                      <th className="text-left p-4 font-medium">Date</th>
                      <th className="text-left p-4 font-medium">Clip</th>
                      <th className="text-left p-4 font-medium">Your Vote</th>
                      <th className="text-left p-4 font-medium">
                        Models Revealed
                      </th>
                      <th className="text-right p-4 font-medium">Outcome</th>
                    </tr>
                  </thead>
                  <tbody>
                    {votes.map((vote) => (
                      <tr
                        key={vote.id}
                        className="border-b border-arena-border last:border-0 hover:bg-arena-surface/50"
                      >
                        <td className="p-4 text-sm text-arena-muted whitespace-nowrap">
                          {formatDate(vote.voted_at)}
                        </td>
                        <td className="p-4">
                          <Link
                            to={`/battle/${vote.battle_id}`}
                            className="text-arena-accent hover:underline text-sm"
                          >
                            {vote.clip_title}
                          </Link>
                        </td>
                        <td className="p-4 text-sm">
                          {getVoteLabel(vote.your_vote)}
                        </td>
                        <td className="p-4 text-sm text-arena-muted">
                          {vote.model_a_name} vs {vote.model_b_name}
                        </td>
                        <td
                          className={`p-4 text-right text-sm font-medium capitalize ${getOutcomeStyle(
                            vote.outcome
                          )}`}
                        >
                          {vote.outcome}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between p-4 border-t border-arena-border">
                <div className="text-sm text-arena-muted">
                  Showing {offset + 1}-{Math.min(offset + limit, votesTotal)} of{' '}
                  {votesTotal}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setOffset(Math.max(0, offset - limit))}
                    disabled={!hasPrevPage}
                    className="btn btn-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setOffset(offset + limit)}
                    disabled={!hasNextPage}
                    className="btn btn-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
